import { DateTime } from 'luxon';
import { ItemGeneralLedgerTransactionType } from '../accounting/accounting';

export type QBOCustomer = {
  Id: string;
  DisplayName: string;
  PrimaryEmailAddr?: { Address: string };
};

export type QBOAddress = {
  Line1: string;
  Line2?: string;
};

export interface QBORef {
  value: string;
  name?: string;
}

export type CustomField = {
  DefinitionId: string;
  Name?: string;
  Type: string;
  StringValue?: string;
};

export type SalesItemLineType = {
  Amount: number;
  Description?: string;
  DetailType: 'SalesItemLineDetail';
  SalesItemLineDetail: {
    Qty?: number;
    UnitPrice?: number;
    TaxCodeRef?: QBORef;
    ItemRef?: QBORef;
  };
};

export type GroupLineType = {
  DetailType: string;
  Description: string;
  GroupLineDetail: {
    Quantity: number;
    GroupItemRef: QBORef;
  };
};

export type DiscountLineType = {
  Amount: number;
  DetailType: 'DiscountLineDetail';
  DiscountLineDetail: {
    PercentBased: boolean;
  };
};

export type LineItem = SalesItemLineType | GroupLineType | DiscountLineType;

export type QBOTxnTaxDetail = {
  TxnTaxCodeRef?: QBORef;
  TotalTax?: number;
  TaxLine?: any[];
};

export type QBOInvoice = {
  Id: string;
  DocNumber: string;
  CustomerRef: QBORef;
  TxnDate?: string;
  Line: LineItem[];
  CustomField: CustomField[];
  BillEmail?: {
    Address: string;
  };
  BillAddr?: QBOAddress;
  ShipAddr?: QBOAddress;
  TxnTaxDetail?: QBOTxnTaxDetail;
  SyncToken?: string;
  LinkedTxn?: {
    TxnId: string;
    TxnType: string;
  }[];
  TotalAmt?: number;
  Balance?: number;
  EmailStatus?: 'NotSet' | 'NeedToSend' | 'EmailSent';
};

export type QBOCreditMemo = {
  Id: string;
  DocNumber: string;
  CustomerRef: QBORef;
  TxnDate?: string;
  Line: LineItem[];
  CustomField: CustomField[];
  BillEmail?: {
    Address: string;
  };
  BillAddr?: QBOAddress;
  ShipAddr?: QBOAddress;
  TxnTaxDetail?: QBOTxnTaxDetail;
  SyncToken?: string;
  LinkedTxn?: {
    TxnId: string;
    TxnType: string;
  }[];
  TotalAmt?: number;
  Balance?: number;
  EmailStatus?: 'NotSet' | 'NeedToSend' | 'EmailSent';
};

export type QBOItemBasedExpenseLine = {
  Id?: string;
  DetailType: 'ItemBasedExpenseLineDetail';
  ItemBasedExpenseLineDetail: {
    ItemRef: QBORef;
    Qty: number;
    UnitPrice: number;
  };
  Amount: number;
};

export type QBOAccountBasedExpenseLine = {
  DetailType: 'AccountBasedExpenseLineDetail';
  Amount: number;
  Description?: string;
  AccountBasedExpenseLineDetail: {
    CustomerRef?: QBORef;
    AccountRef: QBORef;
  };
};

export type QBOPurchaseOrder = {
  Id: string;
  APAccountRef: QBORef;
  VendorRef: QBORef;
  Line: (QBOItemBasedExpenseLine | QBOAccountBasedExpenseLine)[];
  SyncToken?: string;
  TxnDate: string;
  DocNumber: string;
  PrivateNote?: string;
  ShipAddr?: QBOAddress;
  VendorAddr?: QBOAddress;
  TotalAmt: number;
};

export interface QBOCompanyInfo {
  CompanyName: string;
  Country: string;
}

export interface QBOConnectionInfo {
  connectedAt: DateTime;
  companyInfo: QBOCompanyInfo;
  issues: string[];
}

export class QBOConfiguration {
  name!: string;
  value!: string;
}

export interface QBOAccount {
  Id: string;
  Name: string;
  Classification: string;
  FullyQualifiedName: string;
  AccountType: string;
}

export enum QBOItemType {
  Product = 'Product',
  LocationFee = 'LocationFee',
  ProductFee = 'ProductFee',
  UpchargePayment = 'Upcharge Payment',
  Refund = 'Refund',
  Freight = 'Freight',
  Coupon = 'Coupon',
  DamageWaiver = 'Damage Waiver',
  PaymentMethod = 'Payment Method',
}

export type QBOItemIncomeAccount = {
  id: string;
  itemType?: QBOItemType;
  itemId?: string;
  incomeAccount?: string;
  transactionType?: ItemGeneralLedgerTransactionType;
};

export interface QBOTaxCode {
  Id: string;
  Name: string;
}

export interface QBOTaxAgency {
  Id: string;
  DisplayName: string;
  TaxAgencyConfig: string;
}
